import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { qk } from '@/shared/api/query-keys'

import { portalGet } from './client'
import type {
  PortalArchitecture,
  PortalBoard,
  PortalCompanyOverview,
  PortalDocument,
  PortalDocumentListItem,
  PortalMe,
  PortalProjectDetail,
  PortalProjectListItem,
  PortalTask,
} from './types'

/**
 * Las queries del portal. **Solo lectura, y de una sola manera.**
 *
 * No es una recomendación: el hook de mutaciones de React Query no aparece en
 * ningún archivo de esta carpeta —hay un test que lee los archivos y lo
 * verifica, junto con todo lo que el portal importa— y la única forma de hacer
 * red es `portalGet`. El resto del árbol del portal consume estos hooks y nada
 * más.
 *
 * `staleTime` generoso a propósito: un cliente abre su portal, lo mira dos
 * minutos y lo cierra. No es una pantalla de uso diario que necesite estar al
 * segundo, y un refetch al volver a la pestaña solo produce parpadeo.
 */

const STALE = 60_000

/**
 * La empresa que se está mirando en modo "ver como cliente".
 *
 * Sale de `?asCompany=` en la URL —es lo que hace compartible el link "mirá lo
 * que ve Nube Andina"— y va en la query key: sin eso, cambiar de empresa
 * mostraría la cache de la anterior, que en una app multicompany es
 * exactamente la fuga que el resto del sistema evita. Para un `client` el
 * parámetro no existe y todo funciona igual que antes.
 */
function useAsCompany(): string | null {
  const [params] = useSearchParams()
  return params.get('asCompany')
}

/** `GET /portal/projects` devuelve `{ projects: [...] }`. */
interface ProjectListResponse {
  projects: PortalProjectListItem[]
}

export function usePortalMe() {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.me({ asCompany }),
    queryFn: () => portalGet<PortalMe>('me', asCompany),
    staleTime: 5 * 60_000,
  })
}

export function usePortalCompany() {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.company({ asCompany }),
    queryFn: () => portalGet<PortalCompanyOverview>('company', asCompany),
    staleTime: STALE,
  })
}

export function usePortalProjects() {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.projects({ asCompany }),
    queryFn: async () => (await portalGet<ProjectListResponse>('projects', asCompany)).projects,
    staleTime: STALE,
  })
}

export function usePortalProject(projectId: string) {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.project(projectId, { asCompany }),
    queryFn: () => portalGet<PortalProjectDetail>(`projects/${projectId}`, asCompany),
    enabled: Boolean(projectId),
    staleTime: STALE,
  })
}

/**
 * Las tareas ya vienen dentro del detalle. Este hook existe para la vista de
 * tareas sola, que no necesita volver a pedir el proyecto entero.
 */
export function usePortalProjectTasks(projectId: string) {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.projectTasks(projectId, { asCompany }),
    queryFn: () => portalGet<PortalTask[]>(`projects/${projectId}/tasks`, asCompany),
    enabled: Boolean(projectId),
    staleTime: STALE,
  })
}

export function usePortalProjectDocuments(projectId: string) {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.projectDocuments(projectId, { asCompany }),
    queryFn: () =>
      portalGet<PortalDocumentListItem[]>(`projects/${projectId}/documents`, asCompany),
    enabled: Boolean(projectId),
    staleTime: STALE,
  })
}

export function usePortalDocument(documentId: string | null) {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.document(documentId ?? '', { asCompany }),
    queryFn: () => portalGet<PortalDocument>(`documents/${documentId}`, asCompany),
    enabled: Boolean(documentId),
    staleTime: STALE,
  })
}

/**
 * El diagrama del portal: solo los componentes marcados visibles y los links
 * entre ellos. Sin repos — no hay ruta que los devuelva.
 */
export function usePortalArchitecture(projectId: string) {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.architecture(projectId, { asCompany }),
    queryFn: () => portalGet<PortalArchitecture>(`projects/${projectId}/architecture`, asCompany),
    enabled: Boolean(projectId),
    staleTime: STALE,
    // Pantalla de lectura larga: un refetch reacomodaría nodos bajo el cursor.
    refetchOnWindowFocus: false,
  })
}

export function usePortalBoard() {
  const asCompany = useAsCompany()

  return useQuery({
    queryKey: qk.portal.board({ asCompany }),
    queryFn: () => portalGet<PortalBoard>('board', asCompany),
    staleTime: STALE,
  })
}
